// help-view.ts — the SECTION-ONLY in-game HELP DOM overlay (HTML/DOM UI shell plan,
// Phase 5). The build panels' "?" button (forms.ini kidcHelp) originally opened the
// in-game manual (data/help.txt) scrolled to a context section: BUILDINGS (structure
// panel), VEHICLES (VTS), PERSONNEL (HRC), Research Upgrades (R&D) — the C++ Help.cpp
// HelpControl.FollowLink("buildings"/…) then a SCROLLABLE manual with NextPage/PrevPage/
// Back/cross-links (Builder.cpp:703 Help(m_idf == kidfBuildInfantry ? "personnel" :
// "vehicles", …)). The DOM port is SECTION-ONLY by design: a small modal showing ONLY the
// requested section — NO full-manual scrolling, NO NextPage/Back. Faithful WI dark theme
// (the DialogForm translucent body over the canvas) + the web title/body faces + a close
// (X) button. Esc / backdrop tap also closes (the canvas Help form is a ShellForm modal
// dismissed to kidcCancel). DISPLAY-ONLY: emits no command, constructs no Message; the sim
// stays paused by the build panel underneath (it OVERLAYS, does not close, the build panel).
//
// FULL FIDELITY (matching the menu HELP): the section now renders WITH its inline unit/
// structure THUMBNAILS (<IMG SRC="*.anir"> → the pack's team-coloured 'help'-strip frame,
// resolved via the threaded resolveHelpImage — the SAME ClientAnimationData path the canvas
// HelpControl draws inline) and its <A HREF="#x"> CROSS-LINKS as clickable accent text that
// SWITCHES the panel to the linked sub-entry (extractHelpEntry) — still section-only (no
// full-manual nav). Unresolvable images → bracketed alt text + console.warn (never crash).
//
// help.txt is an HTML-ish format (Help.cpp:5-12 supported tags): <H1> = a heading on a new
// line (large font), <H2> = a heading/body on a new line (normal font), <BR> = a line break,
// <HR> = a horizontal rule, <A NAME="x"> = a link target (not displayed), <A HREF="#x">label
// </A> = a hyperlink, <IMG SRC="…"> = an animation strip (the unit/structure thumbnail). The
// PARSE of those tags into a block/run model is shared with the menu manual (parseHelpDoc in
// help-manual-view.ts) — this view applies it to the SECTION SLICE only.
//
// A top-level SECTION = the text from the requested <A NAME="anchor"> up to the next TOP-LEVEL
// section anchor. The four request anchors (buildings / vehicles / personnel / upgrades) are
// the top-level manual sections; each contains sub-entry anchors (hq, powergenerator, …) that
// must NOT terminate the section (else BUILDINGS would be just its heading). So the boundary
// is the next anchor in the top-level set {personnel, vehicles, buildings, upgrades, advanced}
// — e.g. buildings → upgrades, vehicles → buildings, personnel → vehicles, upgrades →
// advanced — giving exactly the canvas FollowLink region (HQ, Power Generator, HRC, VTS,
// towers, etc. for BUILDINGS). A cross-link instead opens its SUB-ENTRY (extractHelpEntry):
// the slice from that anchor up to the NEXT anchor of any kind (one focused unit/structure).

import {
  COL_FG,
  COL_ACCENT,
  COL_BORDER_BLACK,
  TEXT_SHADOW,
  el,
  FONT_TITLE,
  FONT_BODY,
  formScale,
  rootBox,
} from '../chrome.ts';
import { ksfxGuiButtonTap } from '../../ui/uikit.ts';
import { parseHelpDoc, type HelpBlock, type HelpRun } from '../help-manual-view.ts';
import { localizeInstructions } from '../../ui/inputverbs.ts';

// the design stage — a fixed-magnification panel sized like the in-game forms (the help
// manual form kidfHelp is ~140 wide; we use a comfortable section-reader footprint).
const FORM = { w: 150, h: 130 };
const CY_TITLE = 12; // the title bar height (a touch taller than the dialog kcyTitle=10)
const BORDER = 1; // the black DrawBorder frame
const PAD = 5; // body inset (design units)

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

// the top-level manual sections — a requested anchor's section runs UP TO the next anchor in
// this set (its sub-entries don't terminate it). Order is the file order; `advanced` is the
// section that follows `upgrades`, so it bounds the last request anchor.
const TOP_LEVEL = ['personnel', 'vehicles', 'buildings', 'upgrades', 'advanced'];

/** /help.txt fetched + cached once (the raw manual text); shared across HelpView instances
 *  AND the full-manual HelpManualOverlayView (the pause-menu HELP) — one fetch, one cache. */
let helpTextCache: string | null = null;
let helpTextPromise: Promise<string> | null = null;
export async function fetchHelpText(): Promise<string> {
  if (helpTextCache !== null) return helpTextCache;
  if (helpTextPromise === null) {
    helpTextPromise = fetch('/help.txt')
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`help.txt ${r.status}`))))
      .then((t) => {
        helpTextCache = t;
        return t;
      })
      .catch((e) => {
        helpTextPromise = null;
        throw e;
      });
  }
  return helpTextPromise;
}

/** A rendered help section: a TITLE (the section <H1>) + the parsed BODY blocks (the same
 *  block/run/img model parseHelpDoc produces for the menu manual, but scoped to the section
 *  slice) with the leading title <H1> + its scroll anchor removed (the title is in the bar). */
export interface HelpSection {
  title: string;
  blocks: HelpBlock[];
}

/** The end offset of a section/entry slice that starts at `start`: the index of the next
 *  matching `<A NAME>` after `start` (`topLevelOnly` ⇒ only a TOP_LEVEL anchor terminates,
 *  for a whole section; otherwise ANY anchor terminates, for a single sub-entry). */
function sliceEnd(help: string, start: number, topLevelOnly: boolean): number {
  const re = /<A NAME="([^"]*)">/gi;
  re.lastIndex = start + 1;
  for (let m = re.exec(help); m !== null; m = re.exec(help)) {
    if (!topLevelOnly || TOP_LEVEL.includes(m[1])) return m.index;
  }
  return help.length;
}

/**
 * Extract the SECTION for a TOP-LEVEL `anchor` (buildings / vehicles / personnel / upgrades):
 * the slice from `<A NAME="anchor">` up to the next TOP_LEVEL anchor, parsed (shared) into the
 * block/run/img model. Returns null if the anchor isn't found.
 */
export function extractHelpSection(help: string, anchor: string): HelpSection | null {
  const start = help.indexOf(`<A NAME="${anchor}">`);
  if (start < 0) return null;
  const slice = help.slice(start, sliceEnd(help, start, true));
  return finishSection(slice, anchor);
}

/**
 * Extract a single SUB-ENTRY for `anchor` (a cross-link target like `warehouse` / `sr98eagle`):
 * the slice from `<A NAME="anchor">` up to the NEXT anchor of any kind — one focused unit or
 * structure entry (its own <H1> + thumbnail + stats). Returns null if the anchor isn't found.
 */
export function extractHelpEntry(help: string, anchor: string): HelpSection | null {
  const start = help.indexOf(`<A NAME="${anchor}">`);
  if (start < 0) return null;
  const slice = help.slice(start, sliceEnd(help, start, false));
  return finishSection(slice, anchor);
}

/** Shared finish: parse the slice + lift its leading <H1> into the section title. */
function finishSection(slice: string, anchor: string): HelpSection {
  const { blocks } = parseHelpDoc(slice);
  while (blocks.length > 0 && blocks[0].kind === 'anchor') blocks.shift();
  let title = '';
  if (blocks.length > 0 && blocks[0].kind === 'h1') {
    title = (blocks.shift() as { text: string }).text;
  }
  return { title: title || anchor, blocks };
}

export interface HelpViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the help.txt anchor of the section to show (buildings / vehicles / personnel / upgrades). */
  anchor: string;
  /** Resolve an inline <IMG SRC="*.anir"> to a PNG data URL (the pack's team-coloured
   *  'help'-strip frame — resolveHelpImageDataUrl). null ⇒ unresolved (alt text + warn).
   *  Absent ⇒ images are dropped (the host couldn't supply a pack — e.g. a headless harness). */
  resolveHelpImage?: (src: string) => string | null;
  /** play a GUI sfx (ksfxGui*) on the close / link press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** close (X) / backdrop tap / Esc → the host unmounts the overlay and RETURNS to the build
   *  panel underneath (the help OVERLAYS the build panel, like the C++ modal Help() over the
   *  build form — the sim stays paused, nothing un-pauses on close). */
  onClose: () => void;
}

export class HelpView {
  private readonly d: HelpViewDeps;
  /** the raw help.txt once fetched (so a cross-link can re-extract a sub-entry without a refetch). */
  private rawHelp: string | null = null;
  /** the extracted section (null until /help.txt resolves / if the anchor is missing). */
  private section: HelpSection | null = null;
  private loadError = false;
  private closed = false;

  constructor(d: HelpViewDeps) {
    this.d = d;
    const s = d.root.style;
    // a fixed, full-window MODAL overlay at the in-game modal z-layer (20), OVER the build
    // panel (also z=20, in its own container — the help root is appended after, so it stacks
    // on top). pointer-events:all so the backdrop/close reaches us (not the build panel below).
    s.position = 'fixed';
    s.inset = '0';
    s.zIndex = '21'; // one above the build-panel modal layer so it overlays it
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden';
    d.root.addEventListener('pointerdown', this.onBackdrop);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
    // fetch + extract the section, then re-render with the real content.
    void fetchHelpText()
      .then((help) => {
        if (this.closed) return;
        this.rawHelp = help;
        this.section = extractHelpSection(help, this.d.anchor);
        if (this.section === null) this.loadError = true;
        this.render();
      })
      .catch(() => {
        if (!this.closed) {
          this.loadError = true;
          this.render();
        }
      });
  }

  private readonly onResize = (): void => {
    this.render();
  };

  // a tap NOT on the panel → close (the ShellForm modal dismiss → kidcCancel).
  private readonly onBackdrop = (e: PointerEvent): void => {
    const stage = this.d.root.querySelector('[data-help-stage]');
    if (stage !== null && e.target instanceof Node && stage.contains(e.target)) return;
    this.close();
  };

  // Esc → close.
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    }
  };

  destroy(): void {
    this.closed = true;
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    this.d.root.removeEventListener('pointerdown', this.onBackdrop);
  }

  private close(): void {
    if (this.closed) return;
    this.closed = true;
    this.d.playSfx?.(ksfxGuiButtonTap);
    this.d.onClose();
  }

  /** Follow a cross-link to its sub-entry (still SECTION-ONLY — no full-manual nav). Re-extracts
   *  the target's focused entry from the cached raw help and re-renders; an unknown target is
   *  inert (warn, no change) so the link is never a dead-end crash. */
  private followLink(target: string): void {
    this.d.playSfx?.(ksfxGuiButtonTap);
    if (this.rawHelp === null) return;
    const entry = extractHelpEntry(this.rawHelp, target);
    if (entry === null) {
      console.warn(`[help] unknown link target "#${target}"`);
      return;
    }
    this.section = entry;
    this.loadError = false;
    this.render();
    // scroll the body back to the top so the linked entry starts in view.
    const body = this.d.root.querySelector('[data-help-body]') as HTMLElement | null;
    if (body !== null) body.scrollTop = 0;
  }

  // ── the design stage (a section-reader panel sized in the in-game form family) ──
  render(): void {
    if (this.closed) return;
    const s = formScale();
    const stageW = FORM.w * s,
      stageH = FORM.h * s;

    // the stage = a DialogForm-style panel: a BLACK DrawBorder frame; inside it a cyan-accent
    // TITLE BAR (the section title) and a translucent body (the canvas shows through, like the
    // confirm / message box). Section-only: the body SCROLLS within the panel if long.
    const stage = el('div', {
      position: 'absolute',
      width: `${stageW}px`,
      height: `${stageH}px`,
      backgroundColor: COL_BORDER_BLACK,
      boxSizing: 'border-box',
    });
    stage.dataset.helpStage = '1';

    // the translucent body (below the title bar, inset by the border).
    const body = el('div', {
      position: 'absolute',
      left: `${BORDER * s}px`,
      top: `${CY_TITLE * s}px`,
      width: `${(FORM.w - 2 * BORDER) * s}px`,
      height: `${(FORM.h - CY_TITLE - BORDER) * s}px`,
      background: 'rgba(0, 0, 0, 0.78)',
      boxSizing: 'border-box',
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: `${PAD * s}px`,
    });
    body.dataset.helpBody = '1';

    // the TITLE BAR — cyan accent, from the top border to CY_TITLE.
    const titleBar = el('div', {
      position: 'absolute',
      left: `${BORDER * s}px`,
      top: `${BORDER * s}px`,
      width: `${(FORM.w - 2 * BORDER) * s}px`,
      height: `${(CY_TITLE - BORDER) * s}px`,
      background: COL_ACCENT,
    });

    // the section title (kifntTitle / Quadtrangle), dark on the cyan bar, LEFT-padded; leave
    // room on the right for the X button.
    const titleText = this.loadError ? 'HELP' : this.section !== null ? this.section.title : 'HELP';
    const title = el('div', {
      position: 'absolute',
      left: `${PAD * s}px`,
      top: '0',
      right: `${(CY_TITLE + 1) * s}px`,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      fontFamily: FONT_TITLE,
      fontSize: `${Math.round(7 * s)}px`,
      fontWeight: '400',
      letterSpacing: '0.04em',
      color: '#0c1a22',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    title.textContent = titleText;
    title.dataset.helpTitle = '1';
    titleBar.append(title);

    // the close (X) button — SNUG in the top-right corner of the title bar (no margin), a
    // square filling the bar's full height.
    const xSize = (CY_TITLE - BORDER) * s;
    const x = el('button', {
      position: 'absolute',
      right: '0',
      top: '0',
      width: `${xSize}px`,
      height: `${xSize}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      background: 'rgba(0,0,0,0.18)',
      color: '#0c1a22',
      fontFamily: FONT,
      fontSize: `${Math.round(7 * s)}px`,
      fontWeight: '700',
      lineHeight: '1',
      cursor: 'pointer',
      padding: '0',
    }) as HTMLButtonElement;
    x.textContent = '×'; // ×
    x.dataset.helpClose = '1';
    x.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
    });
    x.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });
    titleBar.append(x);

    // the body content.
    const fontPx = Math.round(5.5 * s);
    if (this.section === null && !this.loadError) {
      // still loading.
      const loading = el('div', {
        fontFamily: FONT,
        fontSize: `${fontPx}px`,
        color: COL_FG,
        textShadow: shadow,
        opacity: '0.8',
      });
      loading.textContent = 'Loading…';
      loading.dataset.helpLoading = '1';
      body.append(loading);
    } else if (this.loadError || this.section === null) {
      const err = el('div', {
        fontFamily: FONT,
        fontSize: `${fontPx}px`,
        color: COL_FG,
        textShadow: shadow,
      });
      err.textContent = 'Help is unavailable.';
      body.append(err);
    } else {
      // ONE [data-help-text] container holds every rendered block (paragraphs, headings, link
      // labels, images) — its textContent concatenates the section text (the automation seam
      // helpPanelText reads it); images add no text. Block kinds mirror the menu manual.
      const text = el('div', {
        fontFamily: FONT,
        fontSize: `${fontPx}px`,
        lineHeight: '1.45',
        color: COL_FG,
        textShadow: shadow,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      });
      text.dataset.helpText = '1';
      this.renderBlocks(this.section.blocks, text, s);
      body.append(text);
    }

    stage.append(body, titleBar);

    // POSITION the panel DIRECTLY OVER the build panel that invoked it (centered on that
    // panel's rect), like the canvas modal Help over the build form — fall back to
    // screen-centre if the invoking panel isn't found (it's torn down, or a non-panel caller).
    const { width: vw, height: vh } = rootBox(this.d.root);
    const invoker = document.querySelector('[data-buildpanel-stage]') as HTMLElement | null;
    let cx = (vw - stageW) / 2,
      cy = (vh - stageH) / 2;
    if (invoker !== null) {
      // getBoundingClientRect is VIEWPORT-relative; the stage's left/top are ROOT-relative
      // (== viewport in the shipped client; the play pane in the editor) — re-base it.
      const r = invoker.getBoundingClientRect();
      const rr = this.d.root.getBoundingClientRect();
      cx = r.left - rr.left + (r.width - stageW) / 2;
      cy = r.top - rr.top + (r.height - stageH) / 2;
    }
    // clamp on-screen.
    stage.style.left = `${Math.round(Math.min(Math.max(0, cx), Math.max(0, vw - stageW)))}px`;
    stage.style.top = `${Math.round(Math.min(Math.max(0, cy), Math.max(0, vh - stageH)))}px`;

    this.d.root.replaceChildren(stage);
  }

  /** Render the parsed section blocks into the [data-help-text] container: <H1> sub-headings
   *  (the unit/structure names) bold, <H2>/text paragraphs with inline cross-link runs, <IMG>
   *  thumbnails resolved via resolveHelpImage (alt text + warn if unresolved), <HR> a faint
   *  rule. Anchors render nothing (section-only — no scroll targets needed). */
  private renderBlocks(blocks: HelpBlock[], host: HTMLElement, s: number): void {
    let firstHeading = true;
    for (const block of blocks) {
      switch (block.kind) {
        case 'anchor':
          break; // a scroll target — nothing of its own (the title/headings carry the names)
        case 'h1': {
          // <H1> sub-headings (the unit/structure names) are BOLD + a touch of top space
          // (except the first) to set them off from the preceding paragraph.
          const p = el('div', {
            fontWeight: '900',
            marginBottom: `${Math.round(4 * s)}px`,
            marginTop: firstHeading ? '0' : `${Math.round(3 * s)}px`,
          });
          p.textContent = localizeInstructions(block.text);
          host.append(p);
          firstHeading = false;
          break;
        }
        case 'h2':
        case 'text': {
          const p = el('div', { marginBottom: `${Math.round(4 * s)}px` });
          this.renderRuns(block.runs, p);
          host.append(p);
          break;
        }
        case 'hr': {
          const hr = el('div', {
            height: '0',
            margin: `${Math.round(3 * s)}px 0`,
            borderTop: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
          });
          host.append(hr);
          break;
        }
        case 'img': {
          const url = this.d.resolveHelpImage?.(block.src) ?? null;
          if (url !== null) {
            const img = document.createElement('img');
            img.src = url;
            img.alt = block.src;
            Object.assign(img.style, {
              display: 'block',
              margin: `${Math.round(2 * s)}px 0`,
              imageRendering: 'pixelated',
              maxWidth: '100%',
            } as Partial<CSSStyleDeclaration>);
            img.dataset.helpImg = block.src;
            host.append(img);
          } else {
            console.warn(`[help] could not resolve image "${block.src}" — showing alt text`);
            const ph = el('div', {
              fontSize: `${Math.max(7, Math.round(5 * s))}px`,
              opacity: '0.6',
              fontStyle: 'italic',
              margin: `${Math.round(2 * s)}px 0`,
              border: `${Math.max(1, Math.round(s))}px dashed rgba(143,208,224,0.3)`,
              padding: `${Math.round(1 * s)}px ${Math.round(2 * s)}px`,
              display: 'inline-block',
            });
            ph.textContent = `[${block.src}]`;
            ph.dataset.helpImgMissing = block.src;
            host.append(ph);
          }
          break;
        }
      }
    }
  }

  /** Render inline runs into a paragraph: plain text nodes + cross-links (accent + underline,
   *  clicking SWITCHES the panel to the target's sub-entry via followLink — still section-only). */
  private renderRuns(runs: HelpRun[], container: HTMLElement): void {
    for (const run of runs) {
      if (!run.link) {
        container.append(document.createTextNode(localizeInstructions(run.text)));
        continue;
      }
      const a = el('a', {
        color: COL_ACCENT,
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        cursor: 'pointer',
      });
      a.textContent = localizeInstructions(run.text);
      a.dataset.helpLink = run.target;
      a.setAttribute('role', 'link');
      a.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
      });
      a.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.followLink(run.target);
      });
      container.append(a);
    }
  }
}
